// DT_N_01_お知らせ・キャンペーン一覧
'use client';
import MyPageLayout from '@/components/template/MyPageLayout';
import { useRouter } from 'next/navigation';
import NewsMenu from '@/components/news/NewsMenu';
import TitleLabel from '@/components/molecules/TitleLabel';
import { styled } from 'styled-components';
import Picklist from '@/components/atoms/Picklist';
import ContentText from '@/components/atoms/ContentText';
import Tag from '@/components/molecules/Tag';
import Link from 'next/link';
import PagingBar from '@/components/molecules/PagingBar';
import {
  BREAKPOINT_PC,
  BREAKPOINT_SP,
  NewsEventsTabList,
  parseHtmlText,
} from '@/util/const';
import { useEffect, useState } from 'react';
import Loading from '@/components/molecules/Loading';
import { MdSwapVert } from 'react-icons/md';
import useWidth from '@/util/useWidth';
import { integ_informationAttributes } from '@/models/integ_information';
import { cdateJST } from '@/lib/cdate';

export type NewsSearchParamsType = {
  category?: string;
  orderBy?: 'old' | 'new' | '';
  page?: string;
  showCount?: string;
  year?: string;
};
type Props = {
  categoryCount: integ_informationAttributes[] & { total?: number };
  yearCount: integ_informationAttributes[] & { total?: number };
  news: integ_informationAttributes[];
  searchParams?: NewsSearchParamsType;
  totalCount: number;
};

export default function NewsAndCampaignView(props: Props) {
  const router = useRouter();
  const { isMobile } = useWidth();
  const [showLoading, setShowLoading] = useState(false);
  function redirectWithParameter(
    para: NewsSearchParamsType,
    redirect?: boolean,
  ) {
    const newParams = {
      ...props.searchParams,
      ...para,
    };
    if (!para.page) {
      newParams.page = '';
    }
    if (newParams.category == '') {
      delete newParams.category;
    }
    if (newParams.year == '') {
      delete newParams.year;
    }
    if (newParams.orderBy == '') {
      delete newParams.orderBy;
    }
    if (newParams.page == '') {
      delete newParams.page;
    }
    if (newParams.showCount == '') {
      delete newParams.showCount;
    }
    const params = new URLSearchParams(newParams);
    const url = '/news?' + params.toString();
    if (redirect) {
      setShowLoading(true);
      router.push('/news?' + params.toString());
      router.refresh();
    }
    return url;
  }
  useEffect(() => {
    setShowLoading(false);
  }, [props.searchParams]);
  return (
    <MyPageLayout
      breadCrumbItems={[
        { label: 'TOP', url: '/' },
        { label: 'お知らせ・キャンペーン一覧' },
      ]}
      title="NEWS"
      subTitle="お知らせ・キャンペーン一覧"
      tabUrlList={NewsEventsTabList}
      menu={
        <NewsMenu
          categoryCount={props.categoryCount}
          yearCount={props.yearCount}
        ></NewsMenu>
      }
    >
      {showLoading && <Loading></Loading>}
      <MainContent>
        {!isMobile && (
          <TitleLabel type={'h-xs-sp'} $leftbar $underbar>
            お知らせ・キャンペーン一覧
          </TitleLabel>
        )}
        <div className="filters">
          <Picklist
            isHiddenOnMobile={isMobile}
            initValue={props.searchParams?.orderBy || 'new'}
            options={[
              { label: '新しい順', value: 'new' },
              { label: '古い順', value: 'old' },
            ]}
            $size="S"
            onChange={(value) => {
              redirectWithParameter({ orderBy: value as 'old' | 'new' }, true);
            }}
            icon={<MdSwapVert></MdSwapVert>}
          ></Picklist>
          <div className="number-filter">
            <Picklist
              className="no-ellipsis"
              initValue={props.searchParams?.showCount || '5'}
              options={[
                { label: '5件', value: '5' },
                { label: '10件', value: '10' },
                { label: '15件', value: '15' },
              ]}
              $size="S"
              onChange={(value) => {
                redirectWithParameter({ showCount: value }, true);
              }}
            ></Picklist>
            <ContentText
              type={isMobile ? 'article-m' : 'article-l'}
              className="no-wrap"
            >
              /{props.totalCount}件
            </ContentText>
          </div>
        </div>
        <div className="contents">
          {props.news.map((item, index) => (
            <div key={index} className="item">
              <div className="time">
                {item.public_start_day__c && (
                  <ContentText type="label-l">
                    {cdateJST(item.public_start_day__c).format('YYYY/MM/DD')}
                  </ContentText>
                )}
                <Tag
                  small
                  theme={
                    item.category == 'お知らせ'
                      ? 'processing'
                      : item.category == '個人お知らせ'
                      ? 'seminar'
                      : 'approve'
                  }
                >
                  {item.category}
                </Tag>
                {item.category == '個人お知らせ' && (
                  <>
                    <Tag theme="processing" small>
                      {item.notification_type}
                    </Tag>
                    {!item.is_read && (
                      <Tag theme="unread" small>
                        未読
                      </Tag>
                    )}
                  </>
                )}
                {item.public_range__c &&
                  item.public_range__c.split(';').map((it, i) => (
                    <Tag key={i} theme="error" small>
                      {it}
                    </Tag>
                  ))}
              </div>
              <Link href={`${'/news/' + item.uuid__c}`} className="no-theme">
                <ContentText type="article-l" className="omit_line_2">
                  {parseHtmlText(item.title)}
                </ContentText>
              </Link>
            </div>
          ))}
        </div>
        <PagingBar
          current={parseInt(props.searchParams?.page || '1')}
          total={Math.ceil(
            props.totalCount / parseInt(props.searchParams?.showCount || '5'),
          )}
          className="paging-bar"
          renderUrl={(n) => {
            const pageUrl = redirectWithParameter({ page: `${n}` }, false);
            return pageUrl;
          }}
          size="large"
        ></PagingBar>
      </MainContent>
    </MyPageLayout>
  );
}

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  align-items: center;
  @media (max-width: ${BREAKPOINT_SP}) {
    padding-top: 8px;
  }
  .paging-bar {
    justify-content: center;
    margin-top: 32px;
    @media (max-width: ${BREAKPOINT_SP}) {
      margin-top: 0px;
    }
  }
  .filters {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 8px;
    width: 100%;
    .number-filter {
      display: flex;
      flex-direction: row;
      gap: 4px;
      align-items: center;
    }
    .no-wrap {
      white-space: nowrap;
    }
    .no-ellipsis .singleValue {
      width: 2.5em;
    }
  }
  .contents {
    display: flex;
    flex-direction: column;
    margin-top: 12px;
    width: 100%;
    @media (max-width: ${BREAKPOINT_SP}) {
      padding-bottom: 24px;
    }
    .item {
      display: flex;
      flex-direction: column;
      padding: 16px 0px 16px 0px;
      box-sizing: border-box;
      gap: 4px;
      border-bottom: 1px solid var(--border-lightgray-300, #d4d4d8);
      .time {
        display: flex;
        flex-direction: row;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px;
      }

      @media (min-width: ${BREAKPOINT_PC}) {
        .no-theme {
          text-decoration: none;
          color: inherit;
          width: fit-content;
          &:hover {
            color: var(--node-todo, #71717a);
            text-decoration: underline;
          }
        }
      }
      @media (max-width: ${BREAKPOINT_SP}) {
        padding: 8px 0px 8px 0px;
        .no-theme {
          color: inherit;
          width: fit-content;
          &:hover {
            color: var(--node-todo, #71717a);
            text-decoration: underline;
          }
        }
      }
    }
  }
`;
